import React from 'react';
import { Box, Button, Stack, Theme, Typography, useMediaQuery } from '@mui/material';

import RawHtml from '../legacy/components/base/RawHtml';

/* === STATIC DEPENDENCIES ============================================================ */
const GRID_TEMPLATE_AREAS = {
  1: `
      "img1"
    `,
  2: `
    "img1"
    "img2"
  `,
  3: `
    "img1 img1 img2 img2"
    "img3 img3 img3 img3"
  `,
  4: `
    "img1 img1 img2 img2"
    "img3 img3 img4 img4"
  `,
  5: `
    "img1 img1 img2 img2"
    "img3 img3 img3 img3"
    "img4 img4 img5 img5"
  `,
  6: `
    "img1 img1 img2 img2"
    "img3 img3 img4 img4"
    "img5 img5 img6 img6"
  `,
};

const GRID_TEMPLATE_COLUMNS = {
  1: `repeat(1, 1fr)`,
  2: `repeat(1, 1fr)`,
  3: `repeat(4, 1fr)`,
  4: `repeat(4, 1fr)`,
  5: `repeat(4, 1fr)`,
  6: `repeat(4, 1fr)`,
};

const MAX_GRID_HEIGHT = 420;

const GRID_TEMPLATE_ROWS = {
  1: `repeat(1, ${MAX_GRID_HEIGHT}px)`,
  2: `repeat(2, ${MAX_GRID_HEIGHT / 2}px)`,
  3: `repeat(2, ${MAX_GRID_HEIGHT / 2}px)`,
  4: `repeat(2, ${MAX_GRID_HEIGHT / 2}px)`,
  5: `repeat(2, ${MAX_GRID_HEIGHT / 3}px)`,
  6: `repeat(3, ${MAX_GRID_HEIGHT / 3}px)`,
};

const ALIGN_SELF_BY_CONTENT_ALIGNMENT = {
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end',
};

/* === ARTICLE PROPS ================================================================== */
interface Props {
  label?: string /** This property must to be unique */;
  children?: string /** Raw HTML */;
  direction?: 'left' | 'right';
  title?: string;
  subtitle: string;
  description: string;
  images: { src: string; alt: string }[];
  disclaimer?: string;
  cta?: {
    position?: 'top' | 'bottom';
    text?: string;
    url?: string;
  };
  backgroundColor?: string;
  contentAlignment?: 'top' | 'center' | 'bottom';
  containerId?: string;
}

export const Article = ({
  label,
  children,
  direction = 'left',
  title,
  subtitle,
  description,
  images,
  disclaimer,
  cta = {},
  backgroundColor,
  contentAlignment = 'top',
  containerId,
}: Props) => {
  /* === STATES ======================================================================= */
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const gridColumns = images?.length ?? 1;

  const hasCTA = !!cta.text && !!cta.url;
  const ctaPosition = cta?.position ?? 'bottom';

  /* === RENDER ======================================================================= */
  return (
    <Box id={containerId} component="section" bgcolor={backgroundColor} py={{ xs: 5, md: 18.5 }}>
      <Stack
        className="container"
        flexDirection={{ xs: 'column-reverse', md: direction === 'left' ? 'row-reverse' : 'row' }}
        alignItems="flex-start"
        gap={{ xs: 4, md: 10 }}
        px={{ xs: 3, md: 1 }}
      >
        <Box flex={1} width="100%" height="100%">
          <Box
            display="grid"
            height="100%"
            gridTemplateColumns={{ xs: GRID_TEMPLATE_COLUMNS[1], md: GRID_TEMPLATE_COLUMNS[gridColumns] }}
            gridTemplateRows={{ xs: undefined, md: gridColumns > 1 ? GRID_TEMPLATE_ROWS[gridColumns] : undefined }}
            gridTemplateAreas={{ xs: undefined, md: GRID_TEMPLATE_AREAS[gridColumns] }}
            gap={2}
            sx={{
              '& img': {
                width: { xs: '100%' /** Mobile */, sm: '70%' /** Tablet */, md: '100%' /** Desktop */ },
                height: { xs: undefined, md: gridColumns > 1 ? '100%' : undefined },
                aspectRatio: { xs: '1 / 1' /** Maintain aspect ratio (square) on Mobile */, md: undefined },
                mx: { xs: 'auto' /** Center on Mobile */, md: undefined },
              },
            }}
          >
            {images?.map((img, index) => (
              <img
                key={index}
                src={img.src}
                alt={img.alt}
                style={{
                  gridArea: isMobile ? undefined : `img${index + 1}`,
                  borderRadius: 12,
                  objectFit: 'cover',
                }}
              />
            ))}
          </Box>

          <Typography color="#B1B1B2" fontFamily="Gotham-Book" fontSize={14} mt={2.5}>
            {disclaimer}
          </Typography>
        </Box>

        <Stack flex={1} height="100%" alignSelf={ALIGN_SELF_BY_CONTENT_ALIGNMENT[contentAlignment]}>
          {title ? (
            <Typography component="h2" color="primary.light" fontFamily="Gotham-Medium" mb={1}>
              {title.toUpperCase()}
            </Typography>
          ) : null}

          <Typography color="primary.main" fontFamily="Gotham-Medium" fontSize={32} mb={2}>
            {subtitle}
          </Typography>

          <Typography mb={4} fontSize={14} sx={{ '& p': { mb: 0 } }}>
            <RawHtml content={description} />
          </Typography>

          {hasCTA && ctaPosition === 'top' ? (
            <Button id={label /** For Analytics purposes */} variant="contained" size="large" href={cta.url} sx={{ width: '100%', mb: 5 }}>
              {cta.text}
            </Button>
          ) : null}

          {children ? <RawHtml content={children} /> : null}

          {hasCTA && ctaPosition === 'bottom' ? (
            <Button id={label /** For Analytics purposes */} variant="contained" size="large" href={cta.url} sx={{ width: '100%', mt: 2.5 }}>
              {cta.text}
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
};
